// ============================================================================
// Safe Care - Interruption / Réactivation / Résiliation (côté agent)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm / SearchInput
// Pattern : identique à ProduitAdmin.tsx (formulaire dans Tab + tableau en dessous)
// La demande de suspension est déplacée sur /suspension/demande (flux
// demande -> validation sur /suspension/valide).
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import SearchInput from '../../../Components/SearchInput';
import Params from '../../../Types/ParamsPicklist';
import {
  useGetAllSouscriptions,
  useResilierSouscription,
  useControlerImpayes,
  useMarquerEcheancesImpayees,
  useCloturerContratsEchus,
  useGetRupturesAutoEnCours,
  useSuspendreRuptureAuto,
} from '../hooks';
import { SouscriptionSafeCare } from '../types';

// Paramètres de recherche pour les picklists
let searchSouscription: Params = {
  CodeAgence: '',
  Table: 'SOUSCRIPTIONSAFECARE',
  Colonne1: 'IdSouscription',
  Colonne2: 'NomProduit',
  Colagence: '101',
  Choix: 22,
};

const { TextArea } = Input;

const InterruptionSouscriptions = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: souscriptions, isLoading, refetch } = useGetAllSouscriptions('');
  const resilier = useResilierSouscription();
  const controlerImpayes = useControlerImpayes();
  const marquerImpayees = useMarquerEcheancesImpayees();
  const cloturerEchus = useCloturerContratsEchus();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [Data, SetData] = useState<SouscriptionSafeCare[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [modalMotif, setModalMotif] = useState(false);
  const [motif, setMotif] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  // --- Onglet "Ruptures auto" : état remonté au parent car le contenu est rendu HORS du <Form>
  //     (le Form est disabled -> les radios du tableau ne seraient pas cliquables dedans)
  const { data: rupturesRes, isLoading: loadingRuptures } = useGetRupturesAutoEnCours();
  const suspendreRuptureAuto = useSuspendreRuptureAuto();
  const [ruptureRowKeys, setRuptureRowKeys] = useState<React.Key[]>([]);
  const [selectedRupture, setSelectedRupture] = useState<any>(null);
  const [modalSuspendreRupture, setModalSuspendreRupture] = useState(false);
  const [motifRupture, setMotifRupture] = useState('');

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  useEffect(() => {
    // Seules les souscriptions ACTIVE et SUSPENDUE sont actionnables ici
    // (réactiver / résilier) ; RESILIEE et CLOTUREE sont exclues.
    if (souscriptions) {
      SetData(souscriptions.filter((s) => s.Statut === 'ACTIVE' || s.Statut === 'SUSPENDUE'));
    }
  }, [souscriptions]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return Data;
    const term = searchTerm.toLowerCase();
    return Data.filter((d) =>
      [
        d.Numcli,
        d.NOMCLI,
        d.PRECLI,
        d.NomProduit,
        d.NumeroVersion,
        d.NumCptDebit,
        d.Statut,
        d.MotifStatut,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [Data, searchTerm]);

  // ==================== ACTIONS AUTOMATIQUES ====================
  function handleControlerImpayes() {
    SweetAlert.fire({
      title: 'Contrôle automatique des impayés',
      text: 'Lancer le contrôle automatique de toutes les souscriptions ?',
      showCancelButton: true,
      icon: 'question',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        controlerImpayes.mutate(undefined, {
          onSuccess: (res) => {
            setLoading(false);
            if (res?.StatutCode === 200) {
              const actions = res.Result?.Table1 ?? [];
              const nb = actions.length;
              SweetAlert.fire({
                title: nb > 0 ? 'Contrôle effectué' : 'Aucun impayé',
                text: nb > 0
                  ? `${nb} action(s) effectuée(s) : ${actions.map((a: any) => a.Action).join(', ')}`
                  : res.Message ?? 'Aucune action nécessaire.',
                icon: nb > 0 ? 'warning' : 'success',
              });
            } else {
              SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
            }
          },
        });
      }
    });
  }

  function handleMarquerImpayees() {
    SweetAlert.fire({
      title: 'Marquer les échéances impayées',
      text: 'Marquer automatiquement toutes les échéances échues non payées comme impayées ?',
      showCancelButton: true,
      icon: 'warning',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        marquerImpayees.mutate(undefined, {
          onSuccess: (res) => {
            setLoading(false);
            if (res?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            } else {
              SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
            }
          },
        });
      }
    });
  }

  function handleCloturerEchus() {
    SweetAlert.fire({
      title: 'Clôturer les contrats échus',
      text: 'Clôturer automatiquement les contrats dont la date de fin est dépassée ?',
      showCancelButton: true,
      icon: 'question',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        cloturerEchus.mutate(undefined, {
          onSuccess: (res) => {
            setLoading(false);
            if (res?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            } else {
              SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
            }
          },
        });
      }
    });
  }

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? Data.find((d: any) => d.IdSouscription === newSelectedRowKeys?.[0]);
    if (record) {
      form.setFieldsValue(record);
      setSelectedSouscription(record);
    }
    // Activer les boutons selon le statut (Réactiver dépend de selectedSouscription.Statut)
    if (record?.Statut === 'ACTIVE' || record?.Statut === 'SUSPENDUE') {
      setSupActive(false);   // Résilier
    }
    setAnnulActive(false);
    setConsulActive(true);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  function consult() {
    if (selectedSouscription) {
      form.setFieldsValue(selectedSouscription);
    }
    setAnnulActive(false);
    setModifActve(false);
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
  }

  function actualiser() {
    setLoading(true);
    refetch().finally(() => {
      setLoading(false);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    });
  }

  const openModal = () => {
    if (!selectedSouscription) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription', icon: 'warning' });
      return;
    }
    setMotif('');
    setModalMotif(true);
  };

  const confirmAction = () => {
    if (!selectedSouscription) return;
    setLoading(true);
    resilier.mutate(
      {
        idSouscription: selectedSouscription.IdSouscription!,
        motif,
        utiVal: user.coduti ?? '',
      },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalMotif(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            handleAnulle();
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Réponse vide', icon: 'warning' });
          }
        },
        onError: (err: any) => {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        },
      }
    );
  };

  const onFinish = (values: any) => {
    // Côté agent : pas d'enregistrement direct (lecture seule)
  };

  // ==================== ONGLET RUPTURES AUTO EN COURS ====================
  // Fonction de rendu pure : tout l'état est remonté au parent car ce contenu est
  // affiché HORS du <Form disabled> (sinon les radios du tableau sont incliquables).
  const RupturesAutoTab = () => {
    const ruptures = rupturesRes?.StatutCode === 200 ? rupturesRes.Result?.Table1 ?? [] : [];

    const columnsRuptures: ColumnsType<any> = [
      { title: 'Client', dataIndex: 'Numcli', width: 100 },
      { title: 'Nom', dataIndex: 'NOMCLI', width: 120 },
      { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
      { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
      { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100, align: 'right' },
      { title: 'Statut', dataIndex: 'Statut', width: 100 },
      { title: 'Seuil', dataIndex: 'NbImpayesRupture', width: 70, align: 'center' },
      { title: 'Impayés actuel', dataIndex: 'NbImpayeesActuel', width: 110, align: 'center' },
    ];

    const onSelectRupture = (newSelectedRowKeys: React.Key[]) => {
      setRuptureRowKeys(newSelectedRowKeys);
      const record = ruptures.find((r: any) => r.IdSouscription === newSelectedRowKeys[0]) || null;
      setSelectedRupture(record);
    };

    const openSuspendreRupture = () => {
      if (!selectedRupture) {
        SweetAlert.fire({ title: 'Sélectionnez une souscription', icon: 'warning' });
        return;
      }
      setMotifRupture('');
      setModalSuspendreRupture(true);
    };

    const confirmSuspendreRupture = () => {
      if (!selectedRupture) return;
      if (!motifRupture.trim()) {
        SweetAlert.fire({ title: 'Motif obligatoire', icon: 'warning' });
        return;
      }
      setLoading(true);
      suspendreRuptureAuto.mutate(
        { idSouscription: selectedRupture.IdSouscription, motif: motifRupture, utiVal: user.coduti ?? '' },
        {
          onSuccess: (res) => {
            setLoading(false);
            setModalSuspendreRupture(false);
            if (res?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
              setRuptureRowKeys([]);
              setSelectedRupture(null);
            } else {
              SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
            }
          },
          onError: () => setLoading(false),
        }
      );
    };

    return (
      <div>
        {/* Barre d'action pour suspendre la rupture auto */}
        <Box display="flex" fontStyle="inherit" className="border-left" sx={{ p: 1, mb: 2 }}>
          <Space>
            <Button
              type="primary"
              disabled={!selectedRupture}
              onClick={openSuspendreRupture}
            >
              Suspendre la rupture automatique
            </Button>
          </Space>
        </Box>
        <Box fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 2 }}>
          <Spin spinning={loadingRuptures || loading}>
            <Table
              size="small"
              style={{ width: '100%' }}
              rowKey="IdSouscription"
              rowSelection={{ type: 'radio', selectedRowKeys: ruptureRowKeys, onChange: onSelectRupture }}
              columns={columnsRuptures}
              dataSource={ruptures}
              scroll={{ y: 300, x: 10 }}
              pagination={{ defaultPageSize: 10, pageSizeOptions: [5, 10, 20], showSizeChanger: true, position: ['bottomRight'] }}
              locale={{ emptyText: 'Aucune rupture automatique en cours' }}
            />
          </Spin>
        </Box>

        <Modal
          title="Suspension de la rupture automatique"
          open={modalSuspendreRupture}
          onOk={confirmSuspendreRupture}
          onCancel={() => setModalSuspendreRupture(false)}
          okText="Confirmer la suspension"
          cancelText="Annuler"
          okButtonProps={{ loading: loading }}
          centered
          zIndex={1400}
          className="safeCareModal"
        >
          {selectedRupture && (
            <Box sx={{ mb: 2 }}>
              <p>Client : <b>{selectedRupture.NOMCLI} ({selectedRupture.Numcli})</b></p>
              <p>Produit : <b>{selectedRupture.NomProduit}</b></p>
              <p>Impayés actuel : <b>{selectedRupture.NbImpayeesActuel}</b> (seuil : {selectedRupture.NbImpayesRupture})</p>
            </Box>
          )}
          <TextArea
            rows={4}
            value={motifRupture}
            onChange={(e) => setMotifRupture(e.target.value)}
            placeholder="Indiquez le motif de la suspension de la rupture automatique..."
          />
        </Modal>
      </div>
    );
  };

  const columns: ColumnsType<SouscriptionSafeCare> = [
    { title: 'Client', dataIndex: 'Numcli', width: 100 },
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Compte', dataIndex: 'NumCptDebit', width: 120 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Date effet', dataIndex: 'DateEffet', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
    { title: 'Motif statut', dataIndex: 'MotifStatut', ellipsis: true },
  ];

  // ==================== ONGLET FORMULAIRE SOUSCRIPTION (Tab1) ====================
  // Ce tab contient le formulaire en lecture seule (type="label"), comme Tab1 dans ProduitAdmin.tsx
  const FormTab = () => {
    return (
      <div className="tabItem">
        <div>
          <Box fontStyle="inherit" className="border border-left" width="85%" position="absolute" sx={{ p: 1 }}>
            <legend>Souscription en difficulté</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
              <Box fontStyle="inherit">
                <Form.Item name="IdSouscription" hidden>
                  <input type="hidden" />
                </Form.Item>
                <SearchInput
                  width={9}
                  form={form}
                  Titre="Recherche Souscription"
                  label="Souscription"
                  name="NomProduit"
                  Parametre={searchSouscription}
                />
                <InputForm width={9} label="Client" name="Numcli" type="label" />
                <InputForm width={9} label="Version" name="NumeroVersion" type="label" />
              </Box>
              <hr />
              <Box fontStyle="inherit">
                <InputForm width={9} label="Compte" name="NumCptDebit" type="label" />
                <InputForm width={9} label="Cotisation" name="MontantCotisation" type="label" />
                <InputForm width={9} label="Date effet" name="DateEffet" type="label" />
                <InputForm width={9} label="Statut" name="Statut" type="label" />
                <InputForm width={9} label="Motif statut" name="MotifStatut" type="label" />
              </Box>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Souscription',
      children: FormTab(),
    },
    {
      key: '2',
      label: 'Ruptures auto en cours',
      // Contenu rendu hors du <Form disabled> (radios cliquables) — voir sous le tableau principal
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>{window.location.pathname}</b>
      </Typography>
      <Spin spinning={isLoading || loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={!ajoutActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={onFinish}
          onFinishFailed={() => {}}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  onClick={consult}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={supActive}
                  onClick={openModal}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Résilier
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  onClick={actualiser}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  onClick={handleMarquerImpayees}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Marquer impayés
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  onClick={handleControlerImpayes}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Contrôle auto
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  onClick={handleCloturerEchus}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Clôturer échus
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Form>
        <br />
        <br />
        {/* Tableau en dessous du formulaire - visible seulement sur l'onglet Souscription (hors Form pour permettre le clic radio) */}
        {activeTab === '1' && (
          <Box display="flex" fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
            <Spin spinning={isLoading || loading}>
              <div style={{ width: '100%' }}>
                <Input.Search
                  placeholder="Rechercher une souscription..."
                  allowClear
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ marginBottom: 8, width: 300 }}
                />
                <Table
                  size="small"
                  style={{ width: '100%' }}
                  rowKey="IdSouscription"
                  rowSelection={{ ...rowSelection, type: 'radio' }}
                  columns={columns}
                  dataSource={filteredData}
                  scroll={{ y: 250, x: 10 }}
                  pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
                />
              </div>
            </Spin>
          </Box>
        )}
        {/* Contenu des onglets 2 et 3 : rendu HORS du <Form disabled> pour que les
            radios des tableaux soient cliquables (même raison que le tableau principal) */}
        {activeTab === '2' && RupturesAutoTab()}
      </Spin>

      <Modal
        title="Motif de la résiliation"
        open={modalMotif}
        onOk={confirmAction}
        onCancel={() => setModalMotif(false)}
        okText="Confirmer la résiliation"
        cancelText="Annuler"
        okButtonProps={{
          danger: true,
          loading: loading,
        }}
        centered
        zIndex={1400}
        className="safeCareModal"
      >
        <TextArea
          rows={4}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder="Indiquez le motif..."
        />
      </Modal>

    </div>
  );
};

export default InterruptionSouscriptions;